import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import type { PaymentMethodType, PaymentMethodCategory } from '../types'

interface PaymentMethodSelectorProps {
  amount: number
  onSelect: (method: PaymentMethodType, totalWithFee: number) => void
  selectedMethod?: PaymentMethodType
}

// Payment method icons mapping
const paymentIcons: Record<string, string> = {
  qris: '🔲',
  bank_transfer: '🏦',
  credit_card: '💳',
  debit_card: '💳',
  ewallet_gopay: '💚',
  ewallet_ovo: '💜',
  ewallet_dana: '💙',
  ewallet_shopeepay: '🧡',
  ewallet_linkaja: '❤️',
  retail: '🏪',
  paylater: '📅',
}

function PaymentMethodSelector({ amount, onSelect, selectedMethod }: PaymentMethodSelectorProps) {
  const [expanded, setExpanded] = useState<string | null>('E-Money & QRIS')

  const { data, isLoading, error } = useQuery({
    queryKey: ['payment-methods', amount],
    queryFn: () => api.getPaymentMethods(amount),
    enabled: amount > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Auto-select QRIS as default (lowest fee for small transactions)
  useEffect(() => {
    if (data && !selectedMethod) {
      const qrisMethod = data.paymentMethods.find((m) => m.type === 'qris')
      if (qrisMethod) {
        onSelect('qris', qrisMethod.totalAmount)
      }
    }
  }, [data, selectedMethod, onSelect])

  if (isLoading) {
    return (
      <div className="card p-6 space-y-4">
        <h2 className="text-xl font-bold">Metode Pembayaran</h2>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-bold mb-4">Metode Pembayaran</h2>
        <p className="text-error text-sm">Gagal memuat metode pembayaran</p>
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <div className="card p-6 space-y-4">
      <h2 className="text-xl font-bold">Metode Pembayaran</h2>
      
      {/* Payment fee notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          <span className="font-semibold">ℹ️ Biaya Layanan Pembayaran</span><br />
          Biaya layanan berbeda tergantung metode pembayaran yang dipilih, 
          sesuai dengan ketentuan Bank Indonesia.
        </p>
      </div>

      {/* Payment method categories */}
      <div className="space-y-3">
        {data.categories.map((category: PaymentMethodCategory) => (
          <div key={category.category} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Category header */}
            <button
              type="button"
              onClick={() => setExpanded(expanded === category.category ? null : category.category)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-medium text-gray-900">{category.category}</span>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  expanded === category.category ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Payment methods in category */}
            {expanded === category.category && (
              <div className="p-2 space-y-2">
                {category.methods.map((method) => (
                  <button
                    key={method.type}
                    type="button"
                    onClick={() => onSelect(method.type, method.totalAmount)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                      selectedMethod === method.type
                        ? 'border-primary bg-primary bg-opacity-5'
                        : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{paymentIcons[method.type] || '💵'}</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{method.name}</p>
                        <p className="text-xs text-gray-500">{method.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {method.fee > 0 ? (
                        <>
                          <p className="text-sm font-semibold text-gray-900">
                            + {formatCurrency(method.fee)}
                          </p>
                          <p className="text-xs text-gray-500">{method.feeBreakdown}</p>
                        </>
                      ) : (
                        <p className="text-sm font-semibold text-green-600">Gratis</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Selected method summary */}
      {selectedMethod && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-800">Metode pembayaran dipilih:</p>
              <p className="font-semibold text-green-900">
                {paymentIcons[selectedMethod]} {data.paymentMethods.find((m) => m.type === selectedMethod)?.name}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-green-800">Total bayar:</p>
              <p className="text-xl font-bold text-green-900 font-mono">
                {formatCurrency(
                  data.paymentMethods.find((m) => m.type === selectedMethod)?.totalAmount || amount
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentMethodSelector
